import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { TipoBadge } from '../components/common/Badges';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { errMsg, TIPO_LABELS } from '../lib/format';

const EMPTY = { client_id: '', nombre_proyecto: '', descripcion: '', tipo: 'construccion_prefab' };

export default function ProyectosPage() {
  const queryClient = useQueryClient();
  const { canDelete } = useAuth();
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('todos');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const { data: proyectos, isLoading } = useQuery({
    queryKey: ['projects', tipoFilter, search],
    queryFn: async () => {
      const params = {};
      if (tipoFilter !== 'todos') params.tipo = tipoFilter;
      if (search) params.search = search;
      return (await api.get('/projects', { params })).data;
    },
  });

  const { data: clientes } = useQuery({
    queryKey: ['clients', 'todos', ''],
    queryFn: async () => (await api.get('/clients')).data,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editId) return (await api.put(`/projects/${editId}`, form)).data;
      return (await api.post('/projects', form)).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success(editId ? 'Proyecto actualizado' : 'Proyecto creado');
      setDialogOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/projects/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Proyecto eliminado');
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const openNew = () => { setForm(EMPTY); setEditId(null); setDialogOpen(true); };
  const openEdit = (p) => { setForm({ client_id: p.client_id, nombre_proyecto: p.nombre_proyecto, descripcion: p.descripcion || '', tipo: p.tipo }); setEditId(p.id); setDialogOpen(true); };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Proyectos</h2>
          <p className="text-sm text-muted-foreground">Proyectos asociados a clientes</p>
        </div>
        <Button onClick={openNew} data-testid="new-project-button">
          <Plus size={16} className="mr-1.5" /> Nuevo proyecto
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar proyecto..." className="pl-9" data-testid="projects-search-input" />
        </div>
        <Select value={tipoFilter} onValueChange={setTipoFilter}>
          <SelectTrigger className="w-[200px]" data-testid="projects-tipo-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los tipos</SelectItem>
            {Object.entries(TIPO_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-border shadow-[var(--shadow-elev-1)]">
        <CardContent className="p-0">
          {isLoading ? <div className="p-6 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div> : (
            <Table data-testid="projects-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="th-label">Proyecto</TableHead>
                  <TableHead className="th-label">Cliente</TableHead>
                  <TableHead className="th-label">Tipo</TableHead>
                  <TableHead className="th-label">Descripción</TableHead>
                  <TableHead className="th-label w-24">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(proyectos || []).length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Sin proyectos registrados</TableCell></TableRow>
                )}
                {(proyectos || []).map((p) => (
                  <TableRow key={p.id} className="hover:bg-background/40" data-testid={`project-row-${p.id}`}>
                    <TableCell className="font-medium">{p.nombre_proyecto}</TableCell>
                    <TableCell className="text-muted-foreground">{p.cliente_nombre}</TableCell>
                    <TableCell><TipoBadge tipo={p.tipo} /></TableCell>
                    <TableCell className="text-muted-foreground max-w-[300px] truncate">{p.descripcion || '—'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`edit-project-${p.id}`}>
                          <Pencil size={14} />
                        </Button>
                        {canDelete && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => deleteMutation.mutate(p.id)} data-testid={`delete-project-${p.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="project-dialog">
          <DialogHeader><DialogTitle>{editId ? 'Editar proyecto' : 'Nuevo proyecto'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Cliente *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
                <SelectTrigger data-testid="project-client-select"><SelectValue placeholder="Selecciona un cliente" /></SelectTrigger>
                <SelectContent>
                  {(clientes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Nombre del proyecto *</Label>
              <Input value={form.nombre_proyecto} onChange={(e) => setForm((f) => ({ ...f, nombre_proyecto: e.target.value }))} data-testid="project-nombre-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v }))}>
                <SelectTrigger data-testid="project-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPO_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Descripción</Label>
              <Textarea value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} rows={3} data-testid="project-descripcion-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={!form.nombre_proyecto || !form.client_id || saveMutation.isPending} data-testid="save-project-button">
              {editId ? 'Guardar cambios' : 'Crear proyecto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
